import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../../../../context/AuthContext';
import { toast } from 'react-toastify';
import { MSForm } from '../../../Travelbingo';
import { DataLoaderProps } from '../types';

interface TravelbingoData {
    id: number;
    createdAt: string;
    name: string;
    filename: string;
    status: string;
}

const MSFormsLoader: React.FC<DataLoaderProps> = ({ onDataLoaded, onFormSelected, selectedForm }) => {
    const authContext = useContext(AuthContext);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadedData, setLoadedData] = useState<TravelbingoData | null>(null);
    
    // MS Forms state
    const [forms, setForms] = useState<MSForm[]>([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [formsError, setFormsError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    if (!authContext) {
        throw new Error('MSFormsLoader must be used within an AuthProvider');
    }

    const { token } = authContext;

    // Load MS Forms on mount
    useEffect(() => {
        loadForms();
    }, [token]);

    const loadForms = async () => {
        setFormsLoading(true);
        setFormsError(null);
        try {
            const response = await fetch('/api/msforms', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setFormsError('Bitte melden Sie sich mit Microsoft Office an, um Formulare zu laden.');
                    return;
                }
                const errorData = await response.json();
                throw new Error(errorData.message || 'Fehler beim Laden der Formulare');
            }

            const formsData: MSForm[] = await response.json();
            setForms(formsData);
        } catch (error) {
            console.error('Error loading forms:', error);
            setFormsError(error instanceof Error ? error.message : 'Fehler beim Laden der Formulare');
        } finally {
            setFormsLoading(false);
        }
    };

    const handleFormSelect = (form: MSForm) => {
        setSearchTerm(form.name);
        setShowDropdown(false);
        if (onFormSelected) {
            onFormSelected(form);
        }
        // Mark data as loaded when a form is selected
        onDataLoaded(true);
    };

    const filteredForms = forms.filter(form => 
        form.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleLoadData = async () => {
        if (!name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/api/travelbingo', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: name.trim() })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Laden der Daten');
            }

            const data: TravelbingoData = await response.json();
            setLoadedData(data);
            onDataLoaded(true);
            toast.success('Daten erfolgreich geladen');
        } catch (error) {
            console.error('Error loading travelbingo data:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Daten');
            onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {/* MS Forms Selection Section */}
            <div style={{ marginTop: '20px', marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', color: '#495057' }}>
                    Microsoft Forms Auswählen
                </h3>
                
                {formsError ? (
                    <div style={{ color: '#dc3545', marginBottom: '12px' }}>
                        {formsError}
                        <button 
                            onClick={loadForms}
                            style={{ 
                                marginLeft: '12px',
                                padding: '4px 12px',
                                fontSize: '14px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Erneut versuchen
                        </button>
                    </div>
                ) : (
                    <div style={{ position: 'relative' }}>
                        <label htmlFor="forms-search" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            Formular suchen:
                        </label>
                        <input
                            id="forms-search"
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setShowDropdown(true);
                            }}
                            onFocus={() => setShowDropdown(true)}
                            onBlur={() => {
                                // Delay hiding dropdown to allow click events on dropdown items
                                setTimeout(() => setShowDropdown(false), 200);
                            }}
                            placeholder={formsLoading ? 'Lade Formulare...' : 'Formularname eingeben...'}
                            disabled={formsLoading}
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                                padding: '10px 12px',
                                fontSize: '16px',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                boxSizing: 'border-box'
                            }}
                        />
                        
                        {/* Dropdown list */}
                        {showDropdown && !formsLoading && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                maxWidth: '400px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                backgroundColor: 'white',
                                border: '1px solid #ced4da',
                                borderTop: 'none',
                                borderRadius: '0 0 4px 4px',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                zIndex: 10
                            }}>
                                {filteredForms.length > 0 ? (
                                    filteredForms.map((form) => (
                                        <div
                                            key={form.id}
                                            onClick={() => handleFormSelect(form)}
                                            style={{
                                                padding: '10px 12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #eee',
                                                backgroundColor: selectedForm?.id === form.id ? '#e7f3ff' : 'white'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = selectedForm?.id === form.id ? '#e7f3ff' : 'white';
                                            }}
                                        >
                                            <div style={{ fontWeight: 500 }}>{form.name}</div>
                                            {form.lastModifiedDateTime && (
                                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                    Geändert: {new Date(form.lastModifiedDateTime).toLocaleDateString('de-DE')}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '10px 12px', color: '#6c757d' }}>
                                        {forms.length === 0 ? 'Keine Formulare gefunden' : 'Keine passenden Formulare'}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                {selectedForm && (
                    <div style={{ 
                        marginTop: '16px', 
                        padding: '12px', 
                        backgroundColor: '#d4edda', 
                        border: '1px solid #c3e6cb',
                        borderRadius: '4px',
                        color: '#155724'
                    }}>
                        <strong>Ausgewähltes Formular:</strong> {selectedForm.name}
                    </div>
                )}
            </div>
            
            {/* Divider */}
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                margin: '20px 0',
                color: '#6c757d'
            }}>
                <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #dee2e6' }} />
                <span style={{ padding: '0 16px', fontSize: '14px' }}>ODER</span>
                <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #dee2e6' }} />
            </div>

            {/* Manual data loading section */}
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <label htmlFor="travelbingo-name" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Name:
                </label>
                <input
                    id="travelbingo-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name eingeben"
                    disabled={loading || loadedData !== null}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '8px',
                        fontSize: '16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                    }}
                />
            </div>

            <button
                onClick={handleLoadData}
                disabled={loading || loadedData !== null || !name.trim()}
                style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: loadedData !== null ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading || loadedData !== null || !name.trim() ? 'not-allowed' : 'pointer',
                    opacity: loading || loadedData !== null || !name.trim() ? 0.6 : 1
                }}
            >
                {loading ? 'Wird geladen...' : 'Daten laden'}
            </button>

            {loadedData && (
                <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ marginTop: 0 }}>Geladene Daten:</h3>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>ID:</strong> {loadedData.id}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Name:</strong> {loadedData.name}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Dateiname:</strong> {loadedData.filename}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Status:</strong> {loadedData.status}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Erstellt am:</strong> {new Date(loadedData.createdAt).toLocaleString('de-DE')}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MSFormsLoader;
